import { Box, Button, Typography } from '@mui/material';
import { uniq } from 'lodash';
import { SUPPORTED_LEAGUES } from '../../types/app-config';
import { useAllTeams } from '../../hooks/useAllTeams';
import { useUserAuth } from '../../contexts/auth';
import { useAppTeamConfigs } from '../../hooks/useGetAppConfigs';
import { ConfigLeagueNameOptions } from '../../types';
import getLeagueName from '../../utils/getLeagueName';

const styles = {
  container: {
    px: 1,
  },
  leagueTabs: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    my: 1,
    p: 2,
    background: '#D9E4EF',
    border: '1px solid #CBD5E1',
    borderRadius: '6px',
  },
  leagueTab: {
    minWidth: 30,
    border: 0,
    borderRadius: '6px',
    p: 1,
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.4)',
    },
  },
  leagueInfo: {
    display: 'flex',
    alignItems: 'center',
    mt: 2,
  },
  leagueText: {
    color: '#00000061',
  },
  teamList: {
    gap: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  teamButton: {
    justifyContent: 'flex-start',
    gap: 1,
    alignItems: 'center',
    fontSize: '14px',
    lineHeight: '14px',
    textTransform: 'none',
    borderRadius: '6px',
    fontWeight: 400,
    py: 2,
    '&:hover': {
      background: 'rgba(217, 228, 239, 0.6)',
    },
  },
  logoBox: {
    width: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

interface Props {
  setOpen: (open: boolean) => void;
  selectedAbbrv: string;
  onSelect: (selectedTeamAbbrv: string) => void;
  selectedLeague?: string;
  setSelectedLeague: (selectedLeague?: string) => void;
}

export default function TeamSelector(props: Props) {
  const { selectedAbbrv, onSelect, selectedLeague, setSelectedLeague } = props;

  const { isSuperAdmin } = useUserAuth();
  const { data: teams = [] } = useAllTeams();

  const { data: teamsConfig = [] } = useAppTeamConfigs();

  const availableLeagues = uniq(teamsConfig.map((teamConfig) => teamConfig.data.configLeagueName));

  const supportedLeagues = (() => {
    const output = [
      SUPPORTED_LEAGUES.ALLSVENSKAN,
      SUPPORTED_LEAGUES.SUPERETTAN,
      SUPPORTED_LEAGUES.DAMALLSVENSKAN,
      SUPPORTED_LEAGUES.ELITETTAN,
    ];
    if (isSuperAdmin) {
      output.push(SUPPORTED_LEAGUES.FUTSAL);
    }

    return output;
  })();
  const tabs = supportedLeagues.filter((league) => {
    return availableLeagues.includes(league as ConfigLeagueNameOptions);
  });

  const getLogoFromTeamsData = (abbrv: string) => {
    const teamData = teams?.find((teamData) => {
      return abbrv === teamData.abbrv;
    });
    return teamData?.logoImageUrl;
  };

  return (
    <Box sx={styles.container}>
      <Box sx={{ ...styles.leagueTabs, gap: isSuperAdmin ? 2 : 4 }}>
        {tabs.map((tab) => (
          <Button
            sx={{
              ...styles.leagueTab,
              background: selectedLeague === tab ? 'rgba(255, 255, 255, 1)' : 'none',
            }}
            key={`menu-league-${tab}`}
            onClick={() => {
              setSelectedLeague(tab);
            }}
          >
            <img height={24} src={`/leagues/${tab.toLowerCase()}.png`} alt={tab} />
          </Button>
        ))}
      </Box>
      {selectedLeague ? (
        <Box sx={styles.leagueInfo}>
          <Typography variant="body1" sx={styles.leagueText}>
            {getLeagueName(selectedLeague)}
          </Typography>
        </Box>
      ) : null}
      <Box sx={styles.teamList}>
        {selectedLeague && isSuperAdmin ? (
          <Button
            key={selectedLeague}
            fullWidth
            sx={{
              ...styles.teamButton,
              background:
                selectedLeague?.toUpperCase() === selectedAbbrv ? 'rgba(217, 228, 239, 1)' : 'none',
            }}
            onClick={() => {
              onSelect(selectedLeague?.toUpperCase());
            }}
          >
            <Box sx={styles.logoBox}>
              <img
                src={`/leagues/${selectedLeague}.png`}
                height={16}
                style={{ minHeight: '24px' }}
              />
            </Box>
            {getLeagueName(selectedLeague)} liga
          </Button>
        ) : null}
        {teamsConfig
          .filter(({ data }) => selectedLeague === data.configLeagueName)
          .map(({ abbrv, data }) => (
            <Button
              key={abbrv}
              fullWidth
              sx={{
                ...styles.teamButton,
                background: abbrv === selectedAbbrv ? 'rgba(217, 228, 239, 1)' : 'none',
              }}
              variant="text"
              onClick={() => {
                onSelect(abbrv);
              }}
            >
              <Box sx={styles.logoBox}>
                <img src={getLogoFromTeamsData(abbrv)} height={16} style={{ minHeight: '16px' }} />
              </Box>
              {data.name}
            </Button>
          ))}
      </Box>
    </Box>
  );
}
